import { cva, type VariantProps } from "class-variance-authority";

// Input variants using CVA
export const inputVariants = cva(
  "w-full rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 relative z-0",
  {
    variants: {
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-11 px-4 text-base",
        lg: "h-13 px-5 text-lg",
      },
      variant: {
        default: "border-gray-300 bg-gray-50 focus-visible:ring-prime-700",
        outline: "border-gray-300 bg-white focus-visible:ring-prime-700",
        error: "border-red-500 bg-white focus-visible:ring-red-500",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

// Card variants
export const cardVariants = cva("rounded-lg transition-all duration-200", {
  variants: {
    size: {
      sm: "p-3",
      md: "p-4",
      lg: "p-6",
    },
    variant: {
      default: "bg-white border border-gray-200 shadow-sm",
      elevated: "bg-white shadow-md hover:shadow-lg",
      outlined: "bg-transparent border-2 border-gray-300",
      filled: "bg-prime-50 border-none",
    },
    interactive: {
      true: "cursor-pointer hover:scale-[1.02]",
      false: "",
    },
  },
  defaultVariants: {
    size: "md",
    variant: "default",
    interactive: false,
  },
});

// Typography variants
export const typographyVariants = cva("", {
  variants: {
    variant: {
      h1: "text-4xl font-bold tracking-tight",
      h2: "text-3xl font-semibold tracking-tight",
      h3: "text-2xl font-semibold",
      h4: "text-xl font-medium",
      h5: "text-lg font-medium",
      h6: "text-base font-medium",
      body: "text-base",
      small: "text-sm",
      xs: "text-xs",
      label: "text-sm font-medium",
      caption: "text-xs text-gray-500",
      description: "text-sm text-gray-600 leading-relaxed",
    },
    color: {
      default: "text-gray-900",
      muted: "text-gray-600",
      primary: "text-prime-900",
      error: "text-red-600",
      success: "text-green-600",
    },
  },
  defaultVariants: {
    variant: "body",
    color: "default",
  },
});

// Form field wrapper variants
export const formFieldWrapperVariants = cva("space-y-2", {
  variants: {
    spacing: {
      tight: "space-y-1",
      normal: "space-y-2",
      relaxed: "space-y-4",
    },
  },
  defaultVariants: {
    spacing: "normal",
  },
});

// Button variants (for navigation buttons)
export const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-prime-900 text-white hover:bg-prime-800",
        outline:
          "border border-gray-300 bg-white text-prime-800 hover:bg-prime-200",
        ghost: "hover:bg-gray-100 text-gray-700",
        link: "text-prime-900 underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-9 px-3 text-sm",
        md: "h-11 px-4 text-base",
        lg: "h-13 px-6 text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

// Radio card variants
export const radioCardVariants = cva(
  "rounded-xl flex items-center justify-center cursor-pointer text-center transition-all min-h-[7rem] p-4",
  {
    variants: {
      selected: {
        true: "bg-prime-700/90 scale-[1.0] text-gray-200 border-none",
        false:
          "bg-prime-50 hover:bg-prime-200/80 border border-gray-200 text-prime-950",
      },
    },
    defaultVariants: {
      selected: false,
    },
  }
);

// Export types for variant props
export type InputVariants = VariantProps<typeof inputVariants>;
export type CardVariants = VariantProps<typeof cardVariants>;
export type TypographyVariants = VariantProps<typeof typographyVariants>;
export type FormFieldWrapperVariants = VariantProps<
  typeof formFieldWrapperVariants
>;
export type ButtonVariants = VariantProps<typeof buttonVariants>;
export type RadioCardVariants = VariantProps<typeof radioCardVariants>;
